import * as React from "react";
import { onValue, query, ref, off, DataSnapshot } from "firebase/database";
import * as Sentry from "@sentry/react";
import { rtdb } from "../firebase";
import { IThirtydaysProvider } from "../modules/thirtydays-providers/types/thirtydays-providers";
import { UserRole } from "../utils/types/user";
import { useFetchUserDetails } from "./queries/user";
import { useAppSelector } from "./useRedux";
import { useDoesUserHaveRole } from "./permissions";

export function useThirtydaysProviders() {
    const [providers, setProviders] = React.useState<IThirtydaysProvider[]>([]);

    const { practice } = useAppSelector((state) => state.userPractice);

    React.useEffect(() => {
        const providersRef = query(
            ref(rtdb, `/thirtydaysproviders/${practice?.practice_id}`)
        );

        const handleValueChange = (snapShot: DataSnapshot) => {
            const firebaseProviders = snapShot.val() as Record<
                string,
                Omit<IThirtydaysProvider, "id">
            > | null;
            if (firebaseProviders) {
                const newProviders: IThirtydaysProvider[] = Object.entries(
                    firebaseProviders
                ).map(
                    ([id, data]): IThirtydaysProvider => ({
                        ...data,
                        id,
                    })
                );

                setProviders(newProviders);
            } else {
                setProviders([]);
            }
        };

        onValue(providersRef, handleValueChange, (error) => {
            setProviders([]);
            Sentry.logger.error("Thirty days providers read failed", {
                error_message: error.message,
                practice_id: practice?.practice_id,
            });
        });

        return () => {
            off(providersRef, "value", handleValueChange);
        };
    }, [practice?.practice_id]);

    return { providers };
}

function useHasThirtyDaysProvidersAccess(allowedRoles: UserRole[]) {
    const allowedUserIdsForThirtyDaysProvidersProd = [
        "a06491a7-180a-44d3-8b2d-94dff62fa72a",
        "40fe645e-af8f-4190-b559-ef364e756b97",
    ];

    const allowedUserIdsForThirtyDaysProvidersDev = [
        "9f7023ff-9866-4bf5-a40b-a46ef2fcdb87",
    ];

    const hasAllowedRole = useDoesUserHaveRole(allowedRoles);
    const loggedInUser = useFetchUserDetails();

    const isStaging = import.meta.env.VITE_NODE_ENV !== "production";

    const userIds = isStaging
        ? allowedUserIdsForThirtyDaysProvidersDev
        : allowedUserIdsForThirtyDaysProvidersProd;

    return (
        hasAllowedRole || userIds.includes(loggedInUser?.data?.user_id || "")
    );
}

export function useCanViewThirtyDaysProviders() {
    return useHasThirtyDaysProvidersAccess([
        UserRole.SUPER_ADMIN,
        UserRole.SUPPORT_GENERAL,
        UserRole.SUPPORT_ADMIN,
    ]);
}

export function useCanManageThirtyDaysProviders() {
    return useHasThirtyDaysProvidersAccess([
        UserRole.SUPER_ADMIN,
        UserRole.SUPPORT_ADMIN,
    ]);
}
